import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@src/components/ui/button/button';
import { cn } from '@src/utils/ui';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Select from '@mui/material/Select';
import { MenuItem, InputLabel, FormControl } from '@mui/material';
import toastr from 'toastr';
import { Document } from '@src/views/user/onboarding/documents';

const acceptedFileTypes = ['image/png', 'application/pdf', 'image/jpeg', 'image/jpg'];

const UploadDocument = ({ onSaveFile, disabledFiletypes = [] }) => {
    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <Button
                className={cn(
                    'tw-p-[8px] tw-text-[14px] tw-w-full tw-border-solid tw-border-[1px] tw-border-[#0050C4]'
                )}
                autoFocus
                variant='text'
                onClick={() => setOpen(true)}
            >
                Upload Documents
            </Button>

            {open && (
                <UploadDocumentDialog
                    disabledFiletypes={disabledFiletypes}
                    onSaveFile={onSaveFile}
                    onClose={handleClose}
                />
            )}
        </>
    );
};

const UploadDocumentDialog = ({ disabledFiletypes, onSaveFile, onClose }) => {
    const [firstRender, setFirstRender] = useState(false);
    const [documentType, setDocumentType] = useState('');
    const [file, setFile] = useState(null);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        setFirstRender(true);
    }, []);

    const handleCancel = useCallback(() => {
        handleReset();
        onClose();
    }, []);

    useEffect(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [firstRender]);

    useEffect(() => {
        if (fileInputRef.current) {
            fileInputRef.current.addEventListener('cancel', handleCancel, true);
        }
        return () => {
            if (fileInputRef.current) fileInputRef.current.removeEventListener('cancel', handleCancel, true);
        };
    }, [file, firstRender, handleCancel]);

    const handleChange = e => {
        setDocumentType(e.target.value);
    };

    // Optionally handle file selection
    const handleFileChange = e => {
        const file = e.target.files[0];
        const isInvalidFileType = !acceptedFileTypes.some(type => type === file.type);
        const isExceedFileSize = file && file.size > 5 * 1024 * 1024;
        let errorMessage = null;
        if (isInvalidFileType) {
            errorMessage = 'Invalid file type';
        } else if (isExceedFileSize) {
            errorMessage = 'File size exceeds 5MB limit.';
        }
        if (isExceedFileSize || isInvalidFileType) {
            if (errorMessage) toastr.error(errorMessage);
            handleReset();
            onClose();
            return;
        }
        setFile(file);
    };

    const handleReset = () => {
        setDocumentType('');
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleSave = () => {
        onSaveFile(documentType, file);
        onClose();
        handleReset();
    };

    const handleClose = () => {
        onClose();
    };
    const handleDeleteFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
        }
    };
    return (
        <Dialog
            open
            onClose={handleClose}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
            className='tw-px-[24px] tw-py-[16px]'
        >
            <DialogContent>
                <div className='tw-flex tw-flex-col tw-gap-[16px] tw-text-[16px] tw-text-bcaa-grey-900'>
                    <h6 className='tw-text-[20px] tw-font-medium'>Select Document Type</h6>
                    {file && (
                        <Document
                            doc={{
                                file: file,
                                fileType: ''
                            }}
                            onDelete={handleDeleteFile}
                        />
                    )}
                    <p>
                        Please select the type of document from the list below. We’ll display the document type instead
                        of the file name.
                    </p>
                    {/* d-none file input */}
                    <input
                        type='file'
                        ref={fileInputRef}
                        className='!tw-d-none'
                        accept='.pdf, .png, .jpg, .jpeg'
                        size={5000000} //5MB limit
                        onChange={handleFileChange}
                    />
                    <FormControl>
                        <InputLabel id='doc-select-label'>Document Type</InputLabel>
                        <Select
                            labelId='doc-select-label'
                            id='doc-select'
                            label='Document Type'
                            value={documentType}
                            onChange={handleChange}
                        >
                            <MenuItem
                                value='insurance'
                                className='tw-flew tw-flex-col tw-items-start'
                                disabled={disabledFiletypes.some(fileType => fileType === 'insurance')}
                            >
                                <span>Insurance</span>
                                {disabledFiletypes.some(fileType => fileType === 'insurance') && (
                                    <span className='tw-text-[12px] tw-text-bcaa-grey-700'>
                                        You’ve already selected a document for this type. Only one is allowed.
                                    </span>
                                )}
                            </MenuItem>
                            <MenuItem
                                value='license'
                                className='tw-flew tw-flex-col tw-items-start'
                                disabled={disabledFiletypes.some(fileType => fileType === 'license')}
                            >
                                <span>License</span>
                                {disabledFiletypes.some(fileType => fileType === 'license') && (
                                    <span className='tw-text-[12px] tw-text-bcaa-grey-700'>
                                        You’ve already selected a document for this type. Only one is allowed.
                                    </span>
                                )}
                            </MenuItem>
                            <MenuItem
                                value='certifications'
                                className='tw-flew tw-flex-col tw-items-start'
                                disabled={disabledFiletypes.some(fileType => fileType === 'certifications')}
                            >
                                <span>Certifications</span>
                                {disabledFiletypes.some(fileType => fileType === 'certifications') && (
                                    <span className='tw-text-[12px] tw-text-bcaa-grey-700'>
                                        You’ve already selected a document for this type. Only one is allowed.
                                    </span>
                                )}
                            </MenuItem>
                            <MenuItem
                                value='other'
                                className='tw-flew tw-flex-col tw-items-start'
                                disabled={disabledFiletypes.some(fileType => fileType === 'other')}
                            >
                                <span>Other</span>
                                {disabledFiletypes.some(fileType => fileType === 'other') && (
                                    <span className='tw-text-[12px] tw-text-bcaa-grey-700'>
                                        You’ve already selected a document for this type. Only two is allowed.
                                    </span>
                                )}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </div>
            </DialogContent>
            <DialogActions className='tw-p-[8px]'>
                <Button
                    className={cn('tw-p-[8px] tw-text-[14px] tw-w-full')}
                    autoFocus
                    variant='contained'
                    disabled={documentType === '' || !file}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

UploadDocumentDialog.propTypes = {
    onSaveFile: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    disabledFiletypes: PropTypes.arrayOf(PropTypes.string)
};

UploadDocument.propTypes = {
    onSaveFile: PropTypes.func.isRequired,
    disabledFiletypes: PropTypes.arrayOf(PropTypes.string)
};

export default UploadDocument;
